import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useAuth } from "@/context/AuthContext";
import { API, lessonsApi } from "@/api";
import LessonModal from "@/components/LessonModal";

const HomeScreen = () => {
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [reportPeriod, setReportPeriod] = useState('current_month');
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState('unknown');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isOnline, verifyAndRefreshAuth, pushEnabled, requestNotificationPermission } = useAuth();
  
  const currentLocale = i18n.language?.startsWith('it') ? it : enUS;

  useEffect(() => {
    if ('Notification' in window) {
      setNotificationStatus(Notification.permission);
    } else {
      setNotificationStatus('unsupported');
    }
  }, [pushEnabled]);

  const fetchStats = useCallback(async (month, year) => {
    try {
      setError(null);
      const response = await axios.get(`${API}/dashboard/stats`, {
        params: { month, year }
      });
      setStats(response.data);
    } catch (err) {
      setError(t('home.loadError') || 'Unable to load data. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStats(selectedMonth, selectedYear);
  }, [fetchStats, selectedMonth, selectedYear, location.key]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStats(selectedMonth, selectedYear);
      }
    };

    const handleOnline = () => {
      fetchStats(selectedMonth, selectedYear);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [fetchStats, selectedMonth, selectedYear]);

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const goToCurrentMonth = () => {
    setSelectedMonth(new Date().getMonth() + 1);
    setSelectedYear(new Date().getFullYear());
  };

  const getMonthName = (month, year) => {
    const date = new Date(year, month - 1, 1);
    return format(date, 'MMMM yyyy', { locale: currentLocale });
  };

  const isCurrentMonth = selectedMonth === new Date().getMonth() + 1 && 
                          selectedYear === new Date().getFullYear();

  const handleUpdateLesson = async (lessonId, data) => {
    await lessonsApi.update(lessonId, data);
    setSelectedLesson(null);
    fetchStats(selectedMonth, selectedYear);
  };

  const handleDeleteLesson = async (lessonId) => {
    await lessonsApi.delete(lessonId);
    setSelectedLesson(null);
    fetchStats(selectedMonth, selectedYear);
  };

  const handleCompleteLesson = async (lessonId) => {
    await lessonsApi.complete(lessonId);
    setSelectedLesson(null);
    fetchStats(selectedMonth, selectedYear);
  };

  const handleCancelLesson = async (lessonId) => {
    await lessonsApi.cancel(lessonId);
    setSelectedLesson(null);
    fetchStats(selectedMonth, selectedYear);
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    verifyAndRefreshAuth().then(() => fetchStats(selectedMonth, selectedYear));
  };

  const handleDownloadReport = async () => {
    if (reportPeriod === 'custom' && (!reportStartDate || !reportEndDate)) {
      alert(t('common.selectDates'));
      return;
    }
    
    setReportLoading(true);
    try {
      const response = await axios.post(
        `${API}/reports/download`,
        {
          period: reportPeriod,
          start_date: reportStartDate || null,
          end_date: reportEndDate || null
        },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_vendite_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setShowReportModal(false);
    } catch (err) {
      alert(t('common.downloadError'));
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 pb-20">
      {!isOnline && (
        <div className="bg-amber-500 text-white text-center py-2 text-sm font-medium">
          {t('home.offlineBanner')}
        </div>
      )}

      {(notificationStatus === 'default' || (notificationStatus === 'unknown' && 'Notification' in window)) && (
        <div className="bg-indigo-500 text-white text-center py-3 text-sm font-medium">
          <div className="flex items-center justify-center gap-2 flex-wrap px-4">
            <span>{t('home.notificationsBanner')}</span>
            <button 
              onClick={async () => {
                try {
                  const result = await requestNotificationPermission();
                  if (result) {
                    setNotificationStatus('granted');
                    alert(t('home.notificationsActivated'));
                  } else {
                    if ('Notification' in window && Notification.permission === 'denied') {
                      alert(t('home.notificationsBlocked'));
                      setNotificationStatus('denied');
                    }
                  }
                } catch (err) {
                  alert(t('home.notificationsError'));
                }
              }}
              className="bg-white text-indigo-600 px-4 py-2 rounded-lg font-bold hover:bg-indigo-100"
            >
              {t('home.enableNotifications')}
            </button>
          </div>
          <p className="text-xs text-indigo-200 mt-1">
            {t('home.iphoneTip')}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-500 text-white text-center py-2 text-sm font-medium flex items-center justify-center gap-2">
          <span>{error}</span>
          <button 
            onClick={handleRetry}
            className="bg-white/20 px-3 py-1 rounded-lg hover:bg-white/30"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-6 pb-16">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-indigo-200 text-sm">{t('home.welcome')}, {user?.name || 'Trainer'}!</p>
              <h1 className="text-2xl font-bold">ForeverGym</h1>
            </div>
            <button 
              onClick={() => navigate('/impostazioni')}
              className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-colors"
              title="Impostazioni"
              data-testid="settings-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-10">
        {/* Month Selector */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 flex items-center justify-between">
          <button 
            onClick={goToPreviousMonth}
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          >
            <span className="text-xl">&larr;</span>
          </button>
          <div className="text-center">
            <p className="text-lg font-bold text-gray-900 capitalize">
              {getMonthName(selectedMonth, selectedYear)}
            </p>
            {!isCurrentMonth && (
              <button 
                onClick={goToCurrentMonth}
                className="text-xs text-indigo-600 hover:underline"
              >
                {t('calendar.today')}
              </button>
            )}
          </div>
          <button 
            onClick={goToNextMonth}
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          >
            <span className="text-xl">&rarr;</span>
          </button>
        </div>

        {/* Subscription Limit Banner */}
        {user?.subscription_status !== 'pro' && user?.client_count >= user?.client_limit && (
          <div 
            onClick={() => navigate('/abbonamento')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl shadow-lg p-4 mb-4 cursor-pointer hover:shadow-xl transition-shadow"
            data-testid="upgrade-banner"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">👑</span>
              <div className="flex-1">
                <p className="font-bold">{t('home.upgradeLimit', { limit: user?.client_limit })}</p>
                <p className="text-sm text-white/80">{t('home.upgradeCta')}</p>
              </div>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        )}

        {/* Main Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div 
            className="bg-white rounded-2xl shadow-lg p-5 cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/clienti')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">👥</span>
              </div>
              <span className="text-3xl font-bold text-gray-900">{stats?.total_clients || 0}</span>
            </div>
            <p className="text-gray-600 font-medium">{t('home.totalClients')}</p>
          </div>

          <div 
            className="bg-white rounded-2xl shadow-lg p-5 cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/clienti')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">✅</span>
              </div>
              <span className="text-3xl font-bold text-green-600">{stats?.active_clients || 0}</span>
            </div>
            <p className="text-gray-600 font-medium">{t('home.activeClients') || 'Active Clients'}</p>
          </div>

          <div 
            className="bg-white rounded-2xl shadow-lg p-5 cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => setShowPackagesModal(true)}
            data-testid="earnings-tile"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">💰</span>
              </div>
              <span className="text-2xl font-bold text-emerald-600">€{stats?.earnings_this_month || 0}</span>
            </div>
            <p className="text-gray-600 font-medium">{t('home.monthlyEarnings')}</p>
            {stats?.packages_this_month?.length > 0 && (
              <p className="text-xs text-gray-400 mt-1">{stats.packages_this_month.length} {t('packages.title').toLowerCase()}</p>
            )}
          </div>

          <div 
            className="bg-white rounded-2xl shadow-lg p-5 cursor-pointer hover:shadow-xl transition-shadow"
            onClick={() => navigate('/calendario')}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <span className="text-2xl">🏋️</span>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-purple-600">{stats?.lessons_completed_month || 0}</span>
                <span className="text-lg text-gray-400"> / {stats?.lessons_total_month || 0}</span>
              </div>
            </div>
            <p className="text-gray-600 font-medium">{t('home.completedLessons')}</p>
          </div>
        </div>

        {/* Quick Stats Row */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex justify-around text-center">
            <div>
              <div className="text-2xl font-bold text-indigo-600">{stats?.lessons_scheduled_month || 0}</div>
              <div className="text-xs text-gray-500">{t('lessons.status.scheduled')}</div>
            </div>
            <div className="border-l border-gray-200" />
            <div>
              <div className={`text-2xl font-bold ${stats?.is_current_month ? 'text-indigo-600' : 'text-gray-300'}`}>
                {stats?.is_current_month ? (stats?.lessons_this_week || 0) : '-'}
              </div>
              <div className="text-xs text-gray-500">{t('home.thisWeek') || 'This Week'}</div>
            </div>
            <div className="border-l border-gray-200" />
            <div>
              <div className={`text-2xl font-bold ${stats?.is_current_month ? 'text-indigo-600' : 'text-gray-300'}`}>
                {stats?.is_current_month ? (stats?.todays_lessons?.length || 0) : '-'}
              </div>
              <div className="text-xs text-gray-500">{t('calendar.today')}</div>
            </div>
          </div>
        </div>

        {/* Today's Lessons */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-900 text-lg">{t('home.todayLessons')}</h2>
            <button 
              onClick={() => navigate('/calendario')}
              className="text-indigo-600 text-sm font-medium"
            >
              {t('home.viewAll')} &rarr;
            </button>
          </div>
          {!stats?.is_current_month ? (
            <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
              <div className="text-4xl mb-2">📆</div>
              <p className="text-gray-500">{t('home.viewingMonth') || 'Viewing'} {getMonthName(selectedMonth, selectedYear)}</p>
              <button 
                onClick={goToCurrentMonth}
                className="mt-2 text-indigo-600 text-sm font-medium hover:underline"
              >
                {t('calendar.today')} &rarr;
              </button>
            </div>
          ) : stats?.todays_lessons?.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm p-6 text-center">
              <div className="text-4xl mb-2">📅</div>
              <p className="text-gray-700 font-medium mb-1">{t('home.noLessonsToday')}</p>
              <p className="text-gray-400 text-sm mb-3">{t('home.dayOff') || 'Your day is free!'}</p>
              <button 
                onClick={() => navigate('/calendario')}
                className="text-indigo-600 text-sm font-medium hover:underline"
              >
                + {t('lessons.bookLesson')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {stats?.todays_lessons?.map((lesson) => (
                <div 
                  key={lesson.id || `${lesson.client_id}-${lesson.start_time}`}
                  className="bg-white rounded-2xl shadow-sm p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedLesson({
                    ...lesson,
                    date: format(new Date(), 'yyyy-MM-dd')
                  })}
                  data-testid={`today-lesson-${lesson.id || lesson.start_time}`}
                >
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    lesson.status === 'completed' ? 'bg-green-100' : 
                    lesson.status === 'cancelled' ? 'bg-red-100' : 'bg-indigo-100'
                  }`}>
                    <span className="text-2xl">
                      {lesson.status === 'completed' ? '✅' : 
                       lesson.status === 'cancelled' ? '❌' : '🏋️'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{lesson.client_name}</div>
                    <div className="text-sm text-gray-500">{lesson.start_time} - {lesson.end_time}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      lesson.status === 'completed' 
                        ? 'bg-green-100 text-green-700' 
                        : lesson.status === 'cancelled'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {lesson.status === 'completed' ? t('lessons.status.completed') : 
                       lesson.status === 'cancelled' ? t('lessons.status.cancelled') : t('lessons.status.scheduled')}
                    </div>
                    <span className="text-gray-400">&rarr;</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedLesson && (
          <LessonModal
            lesson={selectedLesson}
            onClose={() => setSelectedLesson(null)}
            onUpdate={handleUpdateLesson}
            onDelete={handleDeleteLesson}
            onComplete={handleCompleteLesson}
            onCancel={handleCancelLesson}
          />
        )}

        {/* Expiring Training Plans Alert */}
        {stats?.expiring_plans?.length > 0 && (
          <div className="mb-6">
            <h2 className="font-bold text-gray-900 text-lg mb-3">Schede in Scadenza</h2>
            <div className="space-y-2">
              {stats.expiring_plans.map((plan) => (
                <div key={plan.id || `${plan.client_id}-${plan.end_date}`} className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                  <span className="text-2xl">📋</span>
                  <div className="flex-1">
                    <div className="font-medium text-amber-900">{plan.client_name}</div>
                    <div className="text-sm text-amber-700">Scade il {format(new Date(plan.end_date), 'd MMM', { locale: it })}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Clients */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-900 text-lg">{t('home.recentClients')}</h2>
            <button 
              onClick={() => navigate('/clienti')}
              className="text-indigo-600 text-sm font-medium"
            >
              {t('home.viewAll')} &rarr;
            </button>
          </div>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            {stats?.recent_clients?.slice(0, 3).map((client, i) => (
              <div 
                key={client.id}
                className={`p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 ${
                  i > 0 ? 'border-t border-gray-100' : ''
                }`}
                onClick={() => navigate(`/cliente/${client.id}`)}
              >
                <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                  {client.first_name[0]}{client.last_name[0]}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{client.first_name} {client.last_name}</div>
                  <div className="text-sm text-gray-500">{client.remaining_lessons} {t('common.lessons')}</div>
                </div>
                <span className="text-gray-400">&rarr;</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button
            onClick={() => navigate('/nuovo-cliente')}
            className="bg-indigo-600 text-white p-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-indigo-700"
          >
            + {t('home.newClient')}
          </button>
          <button
            onClick={() => navigate('/prenota')}
            className="bg-emerald-600 text-white p-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700"
          >
            {t('home.bookLesson')}
          </button>
        </div>

        {/* Download Report Button */}
        <button
          onClick={() => {
            setReportPeriod('current_month');
            setReportStartDate('');
            setReportEndDate('');
            setShowReportModal(true);
          }}
          className="w-full bg-gray-800 text-white p-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-gray-900 mb-6"
          data-testid="download-report-btn"
        >
          {t('reports.downloadReport')}
        </button>
      </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('reports.downloadReport')}</h3>
              <button 
                onClick={() => setShowReportModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              {t('reports.reportDesc')}
            </p>

            <div className="space-y-3 mb-6">
              <label 
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                  reportPeriod === 'current_month' 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="reportPeriod"
                  value="current_month"
                  checked={reportPeriod === 'current_month'}
                  onChange={(e) => setReportPeriod(e.target.value)}
                  className="w-5 h-5 text-indigo-600"
                />
                <div>
                  <div className="font-semibold text-gray-900">{t('reports.currentMonth')}</div>
                  <div className="text-sm text-gray-500">{format(new Date(), 'MMMM yyyy', { locale: currentLocale })}</div>
                </div>
              </label>

              <label 
                className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                  reportPeriod === 'custom' 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="reportPeriod"
                  value="custom"
                  checked={reportPeriod === 'custom'}
                  onChange={(e) => setReportPeriod(e.target.value)}
                  className="w-5 h-5 text-indigo-600 mt-1"
                />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{t('reports.customPeriod')}</div>
                  <div className="text-sm text-gray-500 mb-2">{t('reports.customPeriodDesc')}</div>
                  {reportPeriod === 'custom' && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="text-xs text-gray-500">{t('reports.from')}</label>
                        <input
                          type="date"
                          value={reportStartDate}
                          onChange={(e) => setReportStartDate(e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">{t('reports.to')}</label>
                        <input
                          type="date"
                          value={reportEndDate}
                          onChange={(e) => setReportEndDate(e.target.value)}
                          className="w-full p-2 border border-gray-200 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </label>

              <label 
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                  reportPeriod === 'all' 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="reportPeriod"
                  value="all"
                  checked={reportPeriod === 'all'}
                  onChange={(e) => setReportPeriod(e.target.value)}
                  className="w-5 h-5 text-indigo-600"
                />
                <div>
                  <div className="font-semibold text-gray-900">{t('reports.allHistory')}</div>
                  <div className="text-sm text-gray-500">{t('reports.allHistoryDesc')}</div>
                </div>
              </label>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="text-sm font-medium text-gray-700 mb-2">{t('reports.reportIncludes')}</div>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>{t('reports.totalPackagesPurchased')}</li>
                <li>{t('reports.packagesPerClient')}</li>
                <li>{t('reports.totalLessonsPurchased')}</li>
                <li>{t('reports.lessonsPerClient')}</li>
                <li>{t('reports.totalRevenue')}</li>
              </ul>
            </div>

            <button
              onClick={handleDownloadReport}
              disabled={reportLoading}
              className="w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
              data-testid="confirm-download-report"
            >
              {reportLoading ? t('common.generating') : t('reports.downloadExcel')}
            </button>
          </div>
        </div>
      )}

      {/* Packages Modal */}
      {showPackagesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">{t('home.packagesOfMonth')} {getMonthName(selectedMonth, selectedYear)}</h3>
              <button 
                onClick={() => setShowPackagesModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
                data-testid="close-packages-modal"
              >
                ✕
              </button>
            </div>

            {stats?.packages_this_month?.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-3">📦</div>
                <p className="text-gray-700 font-medium mb-1">{t('home.noPackagesThisMonth')}</p>
                <p className="text-gray-400 text-sm mb-3">{t('home.packagesWillAppearHere')}</p>
                <button 
                  onClick={() => { setShowPackagesModal(false); navigate('/clienti'); }}
                  className="text-indigo-600 text-sm font-medium hover:underline"
                >
                  {t('home.goToClients')} &rarr;
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3 mb-4">
                  {stats?.packages_this_month?.map((pkg, index) => (
                    <div 
                      key={pkg.id || index} 
                      className="bg-gray-50 rounded-xl p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        setShowPackagesModal(false);
                        navigate(`/cliente/${pkg.client_id}`);
                      }}
                      data-testid={`package-item-${index}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-semibold text-gray-900">{pkg.client_name}</div>
                          <div className="text-sm text-gray-500">
                            {pkg.package_type === 'custom' 
                              ? `${t('home.customPackage')} (${pkg.lessons_total} ${t('common.lessons')})` 
                              : pkg.package_type === 'free_trial'
                              ? t('home.freeTrial')
                              : `${pkg.lessons_total} ${pkg.lessons_total === 1 ? t('packages.singleLesson').toLowerCase() : t('common.lessons')}`
                            }
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-emerald-600">€{pkg.price || 0}</div>
                          <div className="text-xs text-gray-400">
                            {pkg.purchased_at ? format(new Date(pkg.purchased_at), 'dd MMM', { locale: currentLocale }) : ''}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`text-xs px-2 py-1 rounded-full ${
                          pkg.lessons_remaining > 0 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {pkg.lessons_remaining}/{pkg.lessons_total} {t('home.remaining')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-700">{t('home.totalOfMonth')} {getMonthName(selectedMonth, selectedYear)}</span>
                    <span className="text-2xl font-bold text-emerald-600">€{stats?.earnings_this_month || 0}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
